import React, { useState } from "react";
import {
  clearSessionMaterials,
  recoverAbandonedSessionMaterials,
} from "../helpers/sessionMaterials";
import { safeUserErrorMessage } from "../helpers/safeErrors";
import {
  cpModalOverlayStyle,
  cpModalPanelStyle,
} from "./shared/crewpilotModalStyles";

const OVERLAY_STYLE = cpModalOverlayStyle({ zIndex: 10000 });

const PANEL_STYLE = cpModalPanelStyle({ maxWidth: "480px" });

function formatCurrency(amount) {
  const value = Number(amount);
  const safe = Number.isFinite(value) ? value : 0;
  return `$${safe.toFixed(2)}`;
}

export function AbandonedSessionMaterialsRecovery({
  drafts = [],
  onResolved,
  onDismiss,
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");

  if (!drafts.length) return null;

  const draft = drafts[activeIndex] || drafts[0];

  async function handleCommit() {
    if (!draft.projectId || !draft.enteredBy) {
      setError(
        "This draft is missing project or user context. Discard it or recover from the original session flow.",
      );
      return;
    }

    setWorking(true);
    setError("");

    try {
      await recoverAbandonedSessionMaterials({
        sessionId: draft.sessionId,
        projectId: draft.projectId,
        enteredBy: draft.enteredBy,
        timeLogId: draft.timeLogId,
        usageDate: draft.usageDate,
        lines: draft.lines,
      });

      const next = drafts.filter((_, index) => index !== activeIndex);
      setActiveIndex(0);
      onResolved?.(next);
    } catch (err) {
      setError(
        safeUserErrorMessage(
          err,
          "Unable to commit recovered materials. Please retry.",
        ),
      );
    } finally {
      setWorking(false);
    }
  }

  function handleDiscard() {
    clearSessionMaterials(draft.sessionId);
    const next = drafts.filter((_, index) => index !== activeIndex);
    setActiveIndex(0);
    onResolved?.(next);
    if (next.length === 0) {
      onDismiss?.();
    }
  }

  return (
    <div style={OVERLAY_STYLE}>
      <div style={PANEL_STYLE}>
        <h3 style={{ marginTop: 0 }}>Recover session materials?</h3>
        <p style={{ opacity: 0.85, lineHeight: 1.5 }}>
          Materials were saved locally for a session but never committed to the
          project ledger.
          {draft.reason === "no_timelog"
            ? " No matching timelog was found — commit only if you know the project."
            : " The session timelog exists — you can commit these materials now."}
        </p>

        <div
          style={{
            background: "rgba(255,255,255,0.04)",
            borderRadius: "12px",
            padding: "12px",
            marginBottom: "12px",
            fontSize: "14px",
            lineHeight: 1.6,
          }}
        >
          <div>
            <strong>{draft.lineCount}</strong> material line
            {draft.lineCount === 1 ? "" : "s"}
          </div>
          <div>Estimated cost: {formatCurrency(draft.estimatedCost)}</div>
          {draft.projectId && (
            <div style={{ opacity: 0.75 }}>Project linked</div>
          )}
        </div>

        {error && (
          <p style={{ color: "#e06b5a", fontSize: "14px" }}>{error}</p>
        )}

        <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
          <button
            type="button"
            className="nx-btn"
            style={{ flex: 1 }}
            disabled={working}
            onClick={handleDiscard}
          >
            Discard
          </button>
          <button
            type="button"
            className="nx-btn nx-btn-gold"
            style={{ flex: 1 }}
            disabled={working || !draft.projectId || !draft.enteredBy}
            onClick={handleCommit}
          >
            {working ? "Committing…" : "Commit to project"}
          </button>
        </div>

        {drafts.length > 1 && (
          <p style={{ opacity: 0.6, fontSize: "12px", marginTop: "12px" }}>
            {activeIndex + 1} of {drafts.length} recovered drafts
          </p>
        )}
      </div>
    </div>
  );
}
